import { X } from "lucide-react";
/**
 * Shared modal / bottom-sheet primitive. Replaces the ~21 hand-rolled
 * `fixed inset-0 z-50 …` overlays across features and fixes their a11y debt in
 * one place: focus trap, Escape-to-close, aria-modal/role=dialog, body
 * scroll-lock, and a thumb-friendly bottom-sheet on phones (centered on ≥sm).
 *
 * Usage:
 *   <Modal open={open} onClose={() => setOpen(false)} title={t('...')}>
 *     ...body...
 *   </Modal>
 */
import { type ReactNode, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDialog } from "../../lib/useDialog.ts";

type ModalSize = "sm" | "md" | "lg";

const SIZE_CLASS: Record<ModalSize, string> = {
	sm: "sm:max-w-sm",
	md: "sm:max-w-md",
	lg: "sm:max-w-lg",
};

export function Modal({
	open,
	onClose,
	title,
	children,
	footer,
	size = "md",
	closeLabel,
}: {
	open: boolean;
	onClose: () => void;
	title?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	size?: ModalSize;
	closeLabel?: string;
}) {
	const { t } = useTranslation("common");
	const ref = useRef<HTMLDivElement>(null);
	const titleId = useId();
	useDialog(ref, onClose, open);

	if (!open || typeof document === "undefined") return null;

	return createPortal(
		<div
			className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div
				ref={ref}
				// biome-ignore lint/a11y/useSemanticElements: shared modal primitive with a custom focus trap, scroll-lock, backdrop-mousedown close, and bottom-sheet layout; swapping to native <dialog> would override this managed behavior and its ::backdrop
				role="dialog"
				aria-modal="true"
				aria-labelledby={title ? titleId : undefined}
				tabIndex={-1}
				className={`w-full ${SIZE_CLASS[size]} max-h-[92vh] overflow-y-auto bg-card shadow-elevated rounded-t-2xl sm:rounded-2xl outline-none animate-slide-up sm:animate-modal-in`}
			>
				{title && (
					<div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 backdrop-blur px-4 py-3">
						<h2
							id={titleId}
							className="text-base font-semibold text-foreground"
						>
							{title}
						</h2>
						<button
							type="button"
							onClick={onClose}
							aria-label={closeLabel ?? t("actions.close")}
							// 44px touch target (WCAG 2.5.5) — the icon stays small, the hit
							// area is thumb-friendly (F4.5).
							className="shrink-0 size-11 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
						>
							<X className="size-4" strokeWidth={2} aria-hidden />
						</button>
					</div>
				)}
				<div className="p-4">{children}</div>
				{footer && (
					<div className="sticky bottom-0 border-t border-border bg-card/95 backdrop-blur px-4 py-3">
						{footer}
					</div>
				)}
			</div>
		</div>,
		document.body,
	);
}
